/**
 * Product key constants, types, and lightweight helpers.
 * No icons, no 3P configs, no React components.
 */

import {
	ProductKeys3P as ProductKeys3PConst,
	ProductsOrder3P,
	NewProductsOrder3P,
} from './3p-product-configs/product-keys-3p';
// oxlint-disable typescript-eslint(consistent-type-imports)
import { PrimaryProductKeys } from './primary-product-keys';
import { type ForgeConnectorProductKey } from './schemas/3p-config';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { PrimaryProductKeys } from './primary-product-keys';

const ProductKeys1P = {
	Atlas: 'townsquare',
	Bitbucket: 'bitbucket',
	Compass: 'compass',
	Confluence: 'confluence',
	Jira: 'jira',
	JiraProductDiscovery: 'jira-product-discovery',
	JiraServiceManagement: 'jira-servicedesk',
	Trello: 'trello',
	Focus: 'mercury',
	Assets: 'cmdb',
	// Can't add these until they're ready as we use this type to enforce that product configs are available for all products
	Talent: 'radar',
	// Opsgenie: 'opsgenie',
} as const;

export const ProductKeys1PForAnalytics: {
	readonly Loom: 'loom';
	readonly Atlas: 'townsquare';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Trello: 'trello';
	readonly Focus: 'mercury';
	readonly Assets: 'cmdb';
	// Can't add these until they're ready as we use this type to enforce that product configs are available for all products
	readonly Talent: 'radar';
	readonly AtlasGoals: 'goals';
	readonly AtlasProjects: 'projects';
	readonly AtlasTeams: 'teams';
} = {
	...ProductKeys1P,
	Loom: 'loom',
	AtlasGoals: 'goals',
	AtlasProjects: 'projects',
	AtlasTeams: 'teams',
} as const;

export const ProductKeys: {
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly OneDrive: 'onedrive';
	readonly Dropbox: 'dropbox';
	readonly Slack: 'slack';
	readonly Sharepoint: 'sharepoint';
	readonly Teams: 'teams';
	readonly GoogleCalendar: 'google-calendar';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly OutlookMail: 'outlook-mail';
	readonly Gmail: 'gmail';
	readonly AdobeSign: 'adobe-sign';
	readonly Asana: 'asana';
	readonly Zendesk: 'zendesk';
	readonly Airtable: 'airtable';
	readonly Smartsheet: 'smartsheet';
	readonly Notion: 'notion';
	readonly Miro: 'miro';
	readonly Docusign: 'docusign';
	readonly Lucid: 'lucid';
	readonly Monday: 'monday';
	readonly AzureDevops: 'azure-devops';
	readonly Gitlab: 'gitlab';
	readonly ConfluenceDC: 'confluence-dc';
	readonly Canva: 'canva';
	readonly Box: 'box';
	readonly Zeplin: 'zeplin';
	readonly ServiceNow: 'servicenow';
	readonly Freshservice: 'freshservice';
	readonly PowerBI: 'power-bi';
	readonly ClickUp: 'clickup';
	readonly Amplitude: 'amplitude';
	readonly Stripe: 'stripe';
	readonly AdobeXD: 'adobexd';
	readonly Aha: 'aha';
	readonly Mural: 'mural';
	readonly Webex: 'webex';
	readonly Todoist: 'todoist';
	readonly PagerDuty: 'pagerduty';
	readonly Pipedrive: 'pipedrive';
	readonly Hubspot: 'hubspot';
	readonly Dovetail: 'dovetail';
	readonly LaunchDarkly: 'launchdarkly';
	readonly Salesforce: 'salesforce';
	readonly Sentry: 'sentry';
	readonly JiraDC: 'jira-dc';
	readonly Gitbook: 'gitbook';
	readonly AWSS3: 'aws-s3';
	readonly Databricks: 'databricks';
	readonly JsmDC: 'jsm-dc';
	readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
	readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
	readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
	readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
	readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
	readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
	readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
	readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
	readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
	readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
	readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
	readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
	readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
	readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
	readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
	readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
	readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
	readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
	readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
	readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
	readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
	readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
	readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
	readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
	readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
	readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
	readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
	readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
	readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
	readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
	readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
	readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
	readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
	readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
	readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
	readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
	readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
	readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
	readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
	readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
	readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
	readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
	readonly Atlas: 'townsquare';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Trello: 'trello';
	readonly Focus: 'mercury';
	readonly Assets: 'cmdb';
	readonly Talent: 'radar';
} = {
	...ProductKeys1P,
	...ProductKeys3PConst,
} as const;

export const ProductKeys3P: {
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly OneDrive: 'onedrive';
	readonly Dropbox: 'dropbox';
	readonly Slack: 'slack';
	readonly Sharepoint: 'sharepoint';
	readonly Teams: 'teams';
	readonly GoogleCalendar: 'google-calendar';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly OutlookMail: 'outlook-mail';
	readonly Gmail: 'gmail';
	readonly AdobeSign: 'adobe-sign';
	readonly Asana: 'asana';
	readonly Zendesk: 'zendesk';
	readonly Airtable: 'airtable';
	readonly Smartsheet: 'smartsheet';
	readonly Notion: 'notion';
	readonly Miro: 'miro';
	readonly Docusign: 'docusign';
	readonly Lucid: 'lucid';
	readonly Monday: 'monday';
	readonly AzureDevops: 'azure-devops';
	readonly Gitlab: 'gitlab';
	readonly ConfluenceDC: 'confluence-dc';
	readonly Canva: 'canva';
	readonly Box: 'box';
	readonly Zeplin: 'zeplin';
	readonly ServiceNow: 'servicenow';
	readonly Freshservice: 'freshservice';
	readonly PowerBI: 'power-bi';
	readonly ClickUp: 'clickup';
	readonly Amplitude: 'amplitude';
	readonly Stripe: 'stripe';
	readonly AdobeXD: 'adobexd';
	readonly Aha: 'aha';
	readonly Mural: 'mural';
	readonly Webex: 'webex';
	readonly Todoist: 'todoist';
	readonly PagerDuty: 'pagerduty';
	readonly Pipedrive: 'pipedrive';
	readonly Hubspot: 'hubspot';
	readonly Dovetail: 'dovetail';
	readonly LaunchDarkly: 'launchdarkly';
	readonly Salesforce: 'salesforce';
	readonly Sentry: 'sentry';
	readonly JiraDC: 'jira-dc';
	readonly Gitbook: 'gitbook';
	readonly AWSS3: 'aws-s3';
	readonly Databricks: 'databricks';
	readonly JsmDC: 'jsm-dc';
	readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
	readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
	readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
	readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
	readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
	readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
	readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
	readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
	readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
	readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
	readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
	readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
	readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
	readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
	readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
	readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
	readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
	readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
	readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
	readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
	readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
	readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
	readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
	readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
	readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
	readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
	readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
	readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
	readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
	readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
	readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
	readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
	readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
	readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
	readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
	readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
	readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
	readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
	readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
	readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
	readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
	readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
} = ProductKeys3PConst;
// TODO: Migrate consumers to import from product-keys-3p.ts directly (Part 4),
// then remove this re-export and this eslint-disable.
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { ProductsOrder3P };
export type ProductKeys3P = (typeof ProductKeys3PConst)[keyof typeof ProductKeys3PConst];
export type ProductKeys1P = (typeof ProductKeys1P)[keyof typeof ProductKeys1P];
export type ProductKeys1PForAnalytics =
	(typeof ProductKeys1PForAnalytics)[keyof typeof ProductKeys1PForAnalytics];
export type ProductKeys = (typeof ProductKeys)[keyof typeof ProductKeys] | ForgeConnectorProductKey;
export const allProductKeys: ProductKeys[] = Object.values(ProductKeys);
export const all3pProductKeys: ProductKeys3P[] = Object.values(ProductKeys3PConst);
export const all1pProductKeys: ProductKeys1P[] = Object.values(ProductKeys1P);
export const is1pProductKey: (key: string) => key is ProductKeys1P = (
	key: string,
): key is ProductKeys1P => all1pProductKeys.includes(key as ProductKeys1P);
export const is3pProductKey: (key: string) => key is ProductKeys3P = (
	key: string,
): key is ProductKeys3P => all3pProductKeys.includes(key as ProductKeys3P);
export const isProductKey: (key?: string) => key is ProductKeys = (
	key?: string,
): key is ProductKeys => {
	if (!key) {
		return false;
	}
	// Check hardcoded keys first
	if (allProductKeys.includes(key as (typeof ProductKeys)[keyof typeof ProductKeys])) {
		return true;
	}
	// Treat any "forge-connector-*" string as valid ProductKeys to support
	// dynamically provisioned Forge connectors without updating this constant list.
	// This means isProductKey is *looser* than "is configured in ProductKeys".
	return key.startsWith('forge-connector-');
};

export const productsSupportingEmptyQuery: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
];

export type PrimaryProductKey = (typeof PrimaryProductKeys)[keyof typeof PrimaryProductKeys];

export const PRODUCTS_ORDER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Assets,
	ProductKeys.Talent,
	...ProductsOrder3P,
];

export const PRODUCTS_ORDER_WITH_3P_BY_POPULARITY: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Atlas,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
	ProductKeys.Compass,
	ProductKeys.Focus,
	ProductKeys.Talent,
	ProductKeys.Assets,
	...NewProductsOrder3P,
];
